import React from "react";
import {
  Box,
  IconButton,
  Typography,
  Divider,
  Avatar,
  Tooltip,
  Chip,
  LinearProgress,
  TextField,
  useTheme,
  CircularProgress,
  Button,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  ArrowBack,
  Menu as MenuIcon,
  OpenInNew as OpenInNewIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Phone as PhoneIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";

import MessageHistory from "../../../components/MessageHistory";
import Placeholder from "../../../components/Placeholder";

import { getChannelIcon, getClientProfilePictureUrl, getInitials } from "../../../utils/utils";
import { useRequests } from "../../../hooks/useRequests";
import { useUsers } from "../../../hooks/useUsers";
import { useAgents } from "../../../hooks/useAgents";
import { useClients } from "../../../hooks/useClients";
import StatusChipSelector, {
  normalizeStatusInput,
} from "../../../components/StatusChipSelector";
import AssigneeSelector from "../../../components/AssigneeSelector";
import { getMessageHistoryWidth } from "../utils/layoutUtils";
import { getWhatsAppWindowState, getLastClientMessageDate } from "../../../utils/whatsappWindowUtils";
import { useTranslation } from "react-i18next";
import { useChannels } from "../../../hooks/useChannels";
import { CHANNEL_TYPES, isWhatsAppQrChannelTipo } from "../../../constants/channelCatalog";
import {
  promotePersonalWhatsappToRequest,
  getConversationById,
  getPersonalChatKeyFromSyntheticConversationId,
  postGupshupVoiceCallPermissionRequest,
  postGupshupTwilioWhatsappOutboundCall,
} from "../../../api/client";
import { useCallContext } from "../../../context/CallContext";
import PromoteToRequestDialog from "../PromoteToRequestDialog";
import DuplicateClientDialog from "../../../components/clients/DuplicateClientDialog";
import { toE164Digits } from "../../../utils/phone";

/** Em falha, fetchData pode lançar o corpo da API `{ message, data }`. */
function formatVoiceChannelApiError(err, fallback) {
  const msg = typeof err?.message === "string" ? err.message : "";
  const d = err?.data && typeof err.data === "object" ? err.data : null;
  const parts = [];
  if (msg) parts.push(msg);
  if (d?.code) parts.push(String(d.code));
  if (d?.error != null) {
    parts.push(
      typeof d.error === "string" ? d.error : JSON.stringify(d.error).slice(0, 900)
    );
  } else if (d?.detail != null) {
    parts.push(
      typeof d.detail === "string" ? d.detail : JSON.stringify(d.detail).slice(0, 900)
    );
  }
  if (Array.isArray(d?.voiceTokenConfigMissing) && d.voiceTokenConfigMissing.length) {
    parts.push(`[${d.voiceTokenConfigMissing.join(", ")}]`);
  }
  const joined = parts.filter(Boolean).join(" — ");
  return joined || fallback;
}

/* ================= MessageHistoryPane ================= */

const MessageHistoryPane = ({
  isMobile,
  containerStyle,
  selectedConversationId,
  selectedConversation,
  showConversationList,
  showConversationDetails,
  userData,
  isLoadingDetails,
  initialLoading,
  isLoadingConversation,
  messagesSyncError,
  reloadConversationMessages,
  messages,
  onSendMessage,
  onEndConversation,
  onOpenPopup,
  onBack,
  onShowDetails,
  onUpdateConversation,
  onNewConversation,
  hasMoreMessages,
  isLoadingMoreMessages,
  onLoadMoreMessages,
  onRefetchConversationDetails,
  onDeleteMessage,
  onDeletePersonalConversation,
  onActivateConversation,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("chat");
  const { prepareTwilioVoiceDevice } = useCallContext();
  const { updateMutation } = useRequests();
  // Precisa incluir ChatBots (ura=1) no seletor de responsável (transferir/atribuir).
  const { data: usersResp } = useUsers(1, 150, "", "todos");
  const { data: agentsResp } = useAgents(1, 200, "");
  const { updateMutation: updateClientMutation } = useClients(
    1,
    10,
    "",
    null,
    { enabled: false }
  );

  const users = usersResp?.users ?? [];
  const agents = agentsResp?.agents ?? [];
  const { data: channelsData } = useChannels(1, 200, "");

  const isPersonalThread =
    selectedConversation?.kind === "personal" ||
    String(selectedConversation?.id || "").startsWith("wa-personal:");

  const [promoteOpen, setPromoteOpen] = React.useState(false);
  const [promoteLoading, setPromoteLoading] = React.useState(false);
  const [promoteError, setPromoteError] = React.useState("");
  const [messagesDupOpen, setMessagesDupOpen] = React.useState(false);
  const [clientDupDialog, setClientDupDialog] = React.useState({
    open: false,
    duplicates: [],
    attemptedPayload: null,
    secondaryClientId: null,
  });

  const [gupshupVoiceBusy, setGupshupVoiceBusy] = React.useState(false);
  const [gupshupVoiceNotice, setGupshupVoiceNotice] = React.useState(null);
  const [composeChannelId, setComposeChannelId] = React.useState(null);
  const [deletePersonalOpen, setDeletePersonalOpen] = React.useState(false);
  const [deletePersonalBusy, setDeletePersonalBusy] = React.useState(false);

  const handleComposeChannelChange = React.useCallback((rawId) => {
    setComposeChannelId(rawId == null || rawId === "" ? null : String(rawId));
  }, []);

  React.useEffect(() => {
    setGupshupVoiceNotice(null);
    setGupshupVoiceBusy(false);
    setComposeChannelId(null);
    setDeletePersonalOpen(false);
    setDeletePersonalBusy(false);
    setMessagesDupOpen(false);
  }, [selectedConversation?.id]);

  React.useEffect(() => {
    if (
      messagesSyncError?.code === "errors.clients.duplicate" &&
      Array.isArray(messagesSyncError.duplicates) &&
      messagesSyncError.duplicates.length
    ) {
      setMessagesDupOpen(true);
    }
  }, [messagesSyncError]);

  React.useEffect(() => {
    setGupshupVoiceNotice(null);
  }, [composeChannelId]);

  const openPromote = () => {
    setPromoteError("");
    setPromoteOpen(true);
  };

  const peerPhone = React.useMemo(() => {
    const chatKey =
      selectedConversation?.personalChatKey ||
      getPersonalChatKeyFromSyntheticConversationId(selectedConversation?.id);
    if (chatKey) {
      const raw = chatKey.startsWith("p:") ? chatKey.slice(2) : chatKey;
      const d = String(raw || "").replace(/\D/g, "");
      return d ? toE164Digits(d, "BR") || d : "";
    }
    const fromConv =
      selectedConversation?.celular ||
      selectedConversation?.phone ||
      selectedConversation?.telefone ||
      "";
    const digits = String(fromConv || "").replace(/\D/g, "");
    return digits ? toE164Digits(digits, "BR") || digits : "";
  }, [
    selectedConversation?.personalChatKey,
    selectedConversation?.id,
    selectedConversation?.celular,
    selectedConversation?.phone,
    selectedConversation?.telefone,
  ]);

  const handlePromoteSubmit = React.useCallback(async (payload) => {
    if (!isPersonalThread || typeof onActivateConversation !== "function") return;
    const chatKey =
      selectedConversation?.personalChatKey ||
      getPersonalChatKeyFromSyntheticConversationId(selectedConversation?.id);
    if (!chatKey) throw new Error(t("pane.promoteError"));

    setPromoteLoading(true);
    setPromoteError("");
    try {
      const res = await promotePersonalWhatsappToRequest({
        chatKey,
        ...payload,
      });
      const convId = res?.data?.conversationId ?? res?.conversationId;
      if (!convId) throw new Error(t("pane.promoteError"));
      const dto = await getConversationById(convId);
      const client = dto?.client || {};
      const conversation = {
        id: dto.id,
        name: client.name ?? dto.name ?? "",
        nome: client.name ?? dto.name ?? "",
        lastMessage: dto.lastMessage || "",
        timestamp: dto.timestamp,
        channel: dto.channel,
        status: dto.status,
        assignedTo: dto.userName ?? dto.assignedTo ?? null,
        unread: dto.unread || 0,
        statusLabel: t("page.statusInProgress"),
      };
      setPromoteOpen(false);
      onActivateConversation(conversation);
    } catch (e) {
      const msg =
        (typeof e?.message === "string" && e.message) ||
        (Array.isArray(e?.message) && e.message[0]) ||
        t("pane.promoteError");
      setPromoteError(msg);
      throw e;
    } finally {
      setPromoteLoading(false);
    }
  }, [isPersonalThread, selectedConversation, onActivateConversation, t]);

  const getWidth = () => {
    if (isMobile) return "100%";
    return getMessageHistoryWidth({
      selectedConversation,
      showConversationList,
      showConversationDetails,
    });
  };

  const displayName =
    selectedConversation?.name || selectedConversation?.nome || "";

  const displayProtocol =
    selectedConversation?.protocol || selectedConversation?.id || "";

  const mergeAttemptedPayload = React.useMemo(() => {
    if (!selectedConversation || isPersonalThread) return {};
    const idProjeto = selectedConversation.projectId ?? selectedConversation.id_projeto ?? null;
    const pid = idProjeto != null && String(idProjeto).trim() !== "" ? Number(idProjeto) : null;
    const onlyDigits = (v) => String(v ?? "").replace(/\D+/g, "");
    const payload = {
      ...(Number.isFinite(pid) ? { id_projeto: pid } : {}),
      nome: selectedConversation.nome || selectedConversation.name || null,
      email: selectedConversation.email || null,
      celular: onlyDigits(selectedConversation.celular) || null,
      telefone: onlyDigits(selectedConversation.telefone || selectedConversation.phone) || null,
      whatsapp: onlyDigits(selectedConversation.whatsapp) || null,
      cpf: onlyDigits(selectedConversation.cpf) || null,
      cnpj: onlyDigits(selectedConversation.cnpj) || null,
      id_interno:
        selectedConversation.id_interno != null && String(selectedConversation.id_interno).trim() !== ""
          ? String(selectedConversation.id_interno).trim()
          : null,
    };
    return payload;
  }, [selectedConversation, isPersonalThread]);

  const mergeSecondaryClientId = React.useMemo(() => {
    const raw = selectedConversation?.clientId;
    if (raw == null || raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }, [selectedConversation?.clientId]);

  const awaitingConversationDetails =
    selectedConversationId != null &&
    !selectedConversation &&
    isLoadingDetails;

  const [isEditingName, setIsEditingName] = React.useState(false);
  const [nameDraft, setNameDraft] = React.useState("");

  React.useEffect(() => {
    if (!selectedConversation) return;
    setIsEditingName(false);
    setNameDraft(selectedConversation?.nome || selectedConversation?.name || "");
  }, [selectedConversation?.id]);

  const canEditName = Boolean(selectedConversation?.clientId) && !isPersonalThread;

  const commitName = (rawValue) => {
    if (!selectedConversation?.clientId) return;
    const next = String(rawValue ?? "").trim();
    if (!next) return;

    const current = String(selectedConversation?.nome || selectedConversation?.name || "").trim();
    if (next === current) {
      setIsEditingName(false);
      return;
    }

    updateClientMutation.mutate(
      { id: selectedConversation.clientId, nome: next },
      {
        onSuccess: () => {
          onUpdateConversation?.({ nome: next, name: next });
          setIsEditingName(false);
        },
        onError: (err) => {
          console.error(t("pane.errClientName"), err);
          if (err?.code === "errors.clients.duplicate") {
            setClientDupDialog({
              open: true,
              duplicates: err?.data?.duplicates || [],
              attemptedPayload: {
                id_projeto:
                  selectedConversation?.projectId ??
                  selectedConversation?.id_projeto ??
                  null,
                nome: next,
              },
              secondaryClientId: Number(selectedConversation.clientId),
            });
          }
        },
      }
    );
  };

  const cancelEditName = () => {
    setIsEditingName(false);
    setNameDraft(selectedConversation?.nome || selectedConversation?.name || "");
  };

  const parseTags = (str) => {
    if (!str || typeof str !== "string") return [];
    return str.split(",").map((t) => t.trim()).filter(Boolean);
  };
  const clientTags = parseTags(
    selectedConversation?.clientTags ?? selectedConversation?.client?.tags ?? ""
  );
  const requestTags = parseTags(selectedConversation?.tags ?? "");
  const hasTags = clientTags.length > 0 || requestTags.length > 0;

  const displayChannel =
    selectedConversation?.channelLabel || selectedConversation?.channel || "";

  const channelType =
    selectedConversation?.channelType ??
    selectedConversation?.channel_type ??
    (() => {
      const ch = (selectedConversation?.channel ?? selectedConversation?.channelLabel ?? "").toString().toLowerCase();
      if (ch.includes("whatsapp")) return "WhatsApp";
      return null;
    })();
  const lastClientDate = getLastClientMessageDate(messages ?? []);
  const channelIdForWindow =
    composeChannelId ??
    selectedConversation?.channelId ??
    selectedConversation?.id_origem ??
    selectedConversation?.channel_id ??
    null;
  const channelRowForWindow = React.useMemo(() => {
    const channelList = Array.isArray(channelsData?.channels) ? channelsData.channels : [];
    if (channelIdForWindow == null) return null;
    return channelList.find((c) => String(c.id) === String(channelIdForWindow)) || null;
  }, [channelsData, channelIdForWindow]);

  const channelTipoForWindow = React.useMemo(() => {
    const t = channelRowForWindow?.tipo;
    return t != null && t !== "" ? Number(t) : null;
  }, [channelRowForWindow]);

  const contactPhoneForVoice =
    selectedConversation?.celular ||
    selectedConversation?.client?.phone ||
    selectedConversation?.phoneNumber ||
    "";

  const showGupshupSipVoiceBar =
    !isPersonalThread &&
    Boolean(selectedConversation) &&
    channelRowForWindow &&
    channelTipoForWindow === CHANNEL_TYPES.WHATSAPP_GUPSHUP &&
    String(channelRowForWindow.whatsapp_calling_mode || "").trim().toLowerCase() === "gupshup_sip";

  const handleGupshupRequestPermission = async () => {
    if (!channelIdForWindow || !contactPhoneForVoice.trim()) {
      setGupshupVoiceNotice({
        severity: "warning",
        text: t("pane.whatsappVoice.missingPhone"),
      });
      return;
    }
    setGupshupVoiceBusy(true);
    setGupshupVoiceNotice(null);
    try {
      await postGupshupVoiceCallPermissionRequest(Number(channelIdForWindow), {
        to: contactPhoneForVoice.trim(),
      });
      setGupshupVoiceNotice({ severity: "success", text: t("pane.whatsappVoice.permissionSent") });
    } catch (e) {
      const msg = formatVoiceChannelApiError(e, t("pane.whatsappVoice.genericError"));
      setGupshupVoiceNotice({ severity: "error", text: String(msg) });
    } finally {
      setGupshupVoiceBusy(false);
    }
  };

  const handleGupshupTwilioCall = async () => {
    const uid = userData?.id;
    if (!channelIdForWindow || !uid) {
      setGupshupVoiceNotice({
        severity: "warning",
        text: t("pane.whatsappVoice.missingUser"),
      });
      return;
    }
    if (!contactPhoneForVoice.trim()) {
      setGupshupVoiceNotice({
        severity: "warning",
        text: t("pane.whatsappVoice.missingPhone"),
      });
      return;
    }
    const agentTo = `client:user_${uid}_ch_${channelIdForWindow}`;
    const leadId = selectedConversation?.id != null ? Number(selectedConversation.id) : null;
    setGupshupVoiceBusy(true);
    setGupshupVoiceNotice(null);
    try {
      if (typeof prepareTwilioVoiceDevice === "function") {
        await prepareTwilioVoiceDevice(Number(channelIdForWindow));
      }
      await postGupshupTwilioWhatsappOutboundCall(Number(channelIdForWindow), {
        to: contactPhoneForVoice.trim(),
        agentTo,
        ...(leadId && !Number.isNaN(leadId) ? { leadId } : {}),
      });
      setGupshupVoiceNotice({ severity: "success", text: t("pane.whatsappVoice.callStarted") });
    } catch (e) {
      const msg = formatVoiceChannelApiError(e, t("pane.whatsappVoice.genericError"));
      setGupshupVoiceNotice({ severity: "error", text: String(msg) });
    } finally {
      setGupshupVoiceBusy(false);
    }
  };
  const whatsAppWindowState =
    channelType &&
    String(channelType).toLowerCase() === "whatsapp" &&
    !isWhatsAppQrChannelTipo(channelTipoForWindow)
      ? getWhatsAppWindowState(lastClientDate)
      : null;
  const showWhatsAppWindowIcon =
    whatsAppWindowState &&
    (whatsAppWindowState.status === "aboutToExpire" || whatsAppWindowState.status === "expired");

  const assignedToValue =
    selectedConversation?.assignedTo ?? selectedConversation?.userName ?? null;

  const assignedIsQueueLabel =
    typeof assignedToValue === "string" &&
    ["fila", "queue"].includes(assignedToValue.toLowerCase());

  const hasUserAssigned =
    assignedToValue !== null &&
    assignedToValue !== undefined &&
    assignedToValue !== 0 &&
    assignedToValue !== "0" &&
    assignedToValue !== "" &&
    !assignedIsQueueLabel;

  const agentName =
    selectedConversation?.agentName ?? selectedConversation?.agent_name ?? null;
  const rawAgentId =
    selectedConversation?.agentId ?? selectedConversation?.id_agente ?? null;
  const hasAgentAssigned =
    rawAgentId !== null &&
    rawAgentId !== "" &&
    rawAgentId !== 0 &&
    rawAgentId !== "0";

  const currentAssignee = React.useMemo(() => {
    if (!selectedConversation) {
      return { type: "queue", id: null, label: t("pane.queue") };
    }

    if (isPersonalThread) {
      return {
        type: "user",
        id: userData?.id ?? null,
        label: t("pane.myRequest"),
      };
    }

    const status = normalizeStatusInput(selectedConversation?.status);
    const isClosed = status === 2 || status === 4 || status === 5;

    if (hasAgentAssigned) {
      const id = rawAgentId;
      const label = agentName || t("pane.aiAgent");
      return { type: "agent", id, label };
    }

    if (hasUserAssigned || selectedConversation?.userId) {
      const id = selectedConversation?.userId ?? null;
      const fromList =
        id != null ? users.find((u) => String(u.id) === String(id)) || null : null;
      const isChatBot =
        fromList
          ? Number(fromList.ura) === 1
          : Number(selectedConversation?.userUra) === 1;
      const baseLabel =
        assignedToValue || selectedConversation?.userName || "";

      const isCurrentUser =
        (id != null && String(id) === String(userData?.id)) ||
        (baseLabel && baseLabel === userData?.nome);

      const label = isCurrentUser
        ? t("pane.myRequest")
        : baseLabel || (isChatBot ? t("pane.chatBot") : t("pane.user"));

      return { type: "user", id, label, isChatBot };
    }

    return {
      type: "queue",
      id: null,
      label: isClosed ? t("pane.unassigned") : t("pane.queue"),
    };
  }, [
    hasAgentAssigned,
    hasUserAssigned,
    agentName,
    assignedToValue,
    t,
    selectedConversation,
    userData,
    users,
    isPersonalThread,
  ]);

  const handleStatusChange = (newStatus) => {
    if (!selectedConversation?.id) return;
    if (isPersonalThread) return;

    updateMutation.mutate(
      { id: selectedConversation.id, status: newStatus },
      {
        onSuccess: () => {
          onUpdateConversation?.({ status: newStatus });
          // Garante que o histórico e demais detalhes da solicitação
          // sejam recarregados imediatamente após a mudança de status.
          if (typeof onRefetchConversationDetails === "function") {
            onRefetchConversationDetails();
          }
        },
        onError: (err) => {
          console.error(t("pane.errStatus"), err);
        },
      }
    );
  };

  const handleAssigneeChange = (next) => {
    if (!selectedConversation?.id || !next) return;
    if (isPersonalThread) return;

    const payload = { id: selectedConversation.id };
    const updates = {};

    if (next.type === "queue") {
      payload.id_usuario = null;
      payload.id_agente = null;
      updates.userId = null;
      updates.assignedTo = null;
      updates.userName = null;
      updates.agentId = null;
      updates.agentName = null;
    } else if (next.type === "agent") {
      // Resolve o ID do agente primeiro a partir do objeto bruto,
      // depois cai para o next.id e, por último, para o nome.
      let agentId = null;

      if (next.raw) {
        const a = next.raw;
        agentId =
          a.id ??
          a.id_agente ??
          a.agentId ??
          null;
      }

      if (!agentId && next.id) {
        agentId = next.id;
      }

      if (!agentId && next.label) {
        const found = agents.find((a) => {
          const name = a.name ?? a.nome ?? "";
          return name === next.label;
        });
        if (found) {
          agentId =
            found.id ??
            found.id_agente ??
            found.agentId ??
            null;
        }
      }

      payload.id_agente = agentId || null;
      payload.id_usuario = null;
      updates.agentId = payload.id_agente;
      updates.agentName = next.label;
      updates.userId = null;
      updates.assignedTo = null;
      updates.userName = null;
    } else if (next.type === "user") {
      payload.id_usuario = next.id ? Number(next.id) : null;
      payload.id_agente = null;
      updates.userId = payload.id_usuario;
      updates.assignedTo = next.label;
      updates.userName = next.label;
      updates.agentId = null;
      updates.agentName = null;
    }

    updateMutation.mutate(payload, {
      onSuccess: () => {
        onUpdateConversation?.(updates);
        if (typeof onRefetchConversationDetails === "function") {
          onRefetchConversationDetails();
        }
      },
      onError: (err) => {
        console.error(t("pane.errAssignee"), err);
      },
    });
  };

  // console.log("messageHistory selectedConversation", selectedConversation);

  return (
    <Box
      className="tour-chat-message-pane"
      width={getWidth()}
      bgcolor={theme.palette.background.paper}
      display="flex"
      flexDirection="column"
      height="100%"
      sx={containerStyle}
    >
      <Box
        className="tour-chat-message-header"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={1}
        gap={0.5}
        sx={{ minWidth: 0 }}
      >
        <Box sx={{ flexShrink: 0 }}>
          {isMobile ? (
            <IconButton onClick={onBack}>
              <ArrowBack />
            </IconButton>
          ) : (
            !showConversationList && (
              <IconButton onClick={onBack}>
                <MenuIcon />
              </IconButton>
            )
          )}
        </Box>

        {selectedConversation && (
          <Box
            display="flex"
            alignItems="center"
            gap={1.5}
            sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}
          >
            <Avatar
              src={
                getClientProfilePictureUrl(
                  selectedConversation?.profile_picture ??
                    selectedConversation?.client?.profile_picture ??
                    selectedConversation?.client?.imagem
                ) || undefined
              }
              sx={{ width: 45, height: 45, flexShrink: 0 }}
            >
              {getInitials(displayName)}
            </Avatar>

            <Box
              display="flex"
              flexDirection="column"
              sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}
            >
              <Box display="flex" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
                {isEditingName ? (
                  <TextField
                    size="small"
                    value={nameDraft}
                    autoFocus
                    onChange={(e) => setNameDraft(e.target.value)}
                    onBlur={() => commitName(nameDraft)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitName(nameDraft);
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        cancelEditName();
                      }
                    }}
                    inputProps={{
                      "aria-label": t("pane.editClientName"),
                      style: { fontWeight: 700 },
                    }}
                    sx={{
                      "& .MuiInputBase-root": { fontSize: "1.2rem", fontWeight: "bold" },
                      minWidth: isMobile ? 0 : 220,
                      flex: 1,
                    }}
                  />
                ) : (
                  <Typography
                    variant="h6"
                    noWrap
                    title={displayName}
                    sx={{
                      fontWeight: "bold",
                      lineHeight: 1.2,
                      cursor: canEditName ? "pointer" : "default",
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    onClick={() => {
                      if (!canEditName) return;
                      setNameDraft(selectedConversation?.nome || selectedConversation?.name || "");
                      setIsEditingName(true);
                    }}
                  >
                    {displayName}
                  </Typography>
                )}

                {canEditName && (
                  isEditingName ? (
                    <Box sx={{ flexShrink: 0, display: "flex" }}>
                      <IconButton
                        size="small"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => commitName(nameDraft)}
                        disabled={updateClientMutation.isLoading}
                        aria-label={t("pane.saveClientName")}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={cancelEditName}
                        disabled={updateClientMutation.isLoading}
                        aria-label={t("pane.cancelClientNameEdit")}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Tooltip title={t("pane.editClientName")}>
                      <span>
                        <IconButton
                          size="small"
                          sx={{ flexShrink: 0 }}
                          onClick={() => {
                            setNameDraft(selectedConversation?.nome || selectedConversation?.name || "");
                            setIsEditingName(true);
                          }}
                          disabled={updateClientMutation.isLoading}
                          aria-label={t("pane.editClientName")}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )
                )}
              </Box>

              <Box mt="auto" display="flex" gap={1} alignItems="center">
                {!isMobile &&
                  !isPersonalThread &&
                  displayChannel &&
                  getChannelIcon(
                    displayChannel,
                    selectedConversation?.channelType || null
                  )}

                {showWhatsAppWindowIcon && (
                  <Tooltip title={whatsAppWindowState.tooltip} placement="bottom" arrow>
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                      <ScheduleIcon
                        sx={{
                          fontSize: 22,
                          color:
                            whatsAppWindowState.status === "aboutToExpire"
                              ? theme.palette.warning.main
                              : theme.palette.error.main,
                        }}
                        aria-label={t("pane.whatsapp24h")}
                      />
                    </Box>
                  </Tooltip>
                )}

                {!isPersonalThread && (
                  <Tooltip title={`${t("pane.protocol")}: ${displayProtocol}`}>
                    <Chip
                      label={`${t("pane.protocol")}: ${displayProtocol}`}
                      size="small"
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: "bold",
                        fontSize: "0.75rem",
                        height: "24px",
                        maxWidth: "120px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    />
                  </Tooltip>
                )}

                {/* Seletor unificado: Fila / Usuário / Agente de IA */}
                {!isMobile && !isPersonalThread && (
                  <AssigneeSelector
                    value={currentAssignee}
                    onChange={handleAssigneeChange}
                    users={users}
                    agents={agents}
                    size="small"
                  />
                )}

                {/* Status como chip clicável com menu de opções */}
                {!isMobile &&
                  !isPersonalThread &&
                  selectedConversation?.status !== null &&
                  selectedConversation?.status !== undefined && (
                    <StatusChipSelector
                      currentStatus={selectedConversation.status}
                      onStatusChange={handleStatusChange}
                    />
                  )}

                {isPersonalThread && (
                  <Chip
                    size="small"
                    label={t("pane.personalWhatsappBadge")}
                    color="success"
                    variant="outlined"
                    sx={{ fontSize: "0.7rem", height: 24 }}
                  />
                )}
              </Box>

              {/* Tags do cliente e da solicitação */}
              {hasTags && (
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gap={0.5}
                  alignItems="center"
                  mt={0.5}
                >
                  {clientTags.map((tag, i) => (
                    <Chip
                      key={`c-${i}`}
                      label={tag}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: "0.7rem",
                        height: 20,
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  ))}
                  {requestTags.map((tag, i) => (
                    <Chip
                      key={`r-${i}`}
                      label={tag}
                      size="small"
                      color="primary"
                      sx={{
                        fontSize: "0.7rem",
                        height: 20,
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 0.5 }}>
          {isPersonalThread && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={openPromote}
              sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
            >
              {t("pane.promoteToRequest")}
            </Button>
          )}
          {isPersonalThread && typeof onDeletePersonalConversation === "function" && (
            <Tooltip title={t("pane.deletePersonalTooltip")}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  aria-label={t("pane.deletePersonalTooltip")}
                  onClick={() => setDeletePersonalOpen(true)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {selectedConversation?.id && !isMobile && !isPersonalThread && (
            <IconButton onClick={onOpenPopup} title={t("pane.openPopup")}>
              <OpenInNewIcon />
            </IconButton>
          )}
          {selectedConversation && !showConversationDetails && !isPersonalThread && (
            <IconButton onClick={onShowDetails}>
              <MenuIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      <Divider />

      {messagesSyncError &&
        selectedConversation &&
        !isPersonalThread &&
        !(messagesDupOpen && messagesSyncError?.code === "errors.clients.duplicate") && (
          <Alert
            severity={
              messagesSyncError.code === "errors.clients.duplicate" ? "warning" : "error"
            }
            sx={{ borderRadius: 0 }}
            action={
              typeof reloadConversationMessages === "function" ? (
                <Button color="inherit" size="small" onClick={() => reloadConversationMessages()}>
                  Tentar novamente
                </Button>
              ) : null
            }
          >
            {messagesSyncError.message ||
              "Não foi possível carregar o histórico desta conversa."}
            {messagesSyncError.code === "errors.clients.duplicate" ? (
              <Typography variant="caption" component="div" sx={{ display: "block", mt: 0.75 }}>
                Abra o aviso de duplicidade para unificar os cadastros ou ajustar o telefone.
              </Typography>
            ) : null}
          </Alert>
        )}

      {showGupshupSipVoiceBar && (
        <Alert
          severity="info"
          icon={<PhoneIcon fontSize="inherit" />}
          sx={{ borderRadius: 0, py: 0.75, alignItems: "center" }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
          >
            <Typography variant="body2" sx={{ flex: 1 }}>
              {t("pane.whatsappVoice.hint")}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="outlined"
                disabled={gupshupVoiceBusy}
                onClick={handleGupshupRequestPermission}
                sx={{ textTransform: "none" }}
              >
                {t("pane.whatsappVoice.requestPermission")}
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={gupshupVoiceBusy}
                onClick={handleGupshupTwilioCall}
                sx={{ textTransform: "none" }}
              >
                {t("pane.whatsappVoice.startCall")}
              </Button>
            </Stack>
          </Stack>
          {gupshupVoiceNotice && (
            <Typography
              variant="caption"
              component="div"
              sx={{
                mt: 1,
                color:
                  gupshupVoiceNotice.severity === "error"
                    ? "error.main"
                    : gupshupVoiceNotice.severity === "warning"
                      ? "warning.main"
                      : "success.main",
              }}
            >
              {gupshupVoiceNotice.text}
            </Typography>
          )}
        </Alert>
      )}

      {isLoadingDetails && !initialLoading && (
        <LinearProgress sx={{ width: "100%", position: "absolute", top: 0 }} />
      )}

      {selectedConversation && (!isLoadingDetails || !initialLoading) ? (
        <MessageHistory
          messages={messages}
          selectedConversation={selectedConversation}
          isLoadingConversation={isLoadingConversation}
          onSendMessage={onSendMessage}
          onEndConversation={onEndConversation}
          onReopenRequest={() => handleStatusChange(1)}
          onOpenNewRequest={onNewConversation}
          hasMoreMessages={hasMoreMessages}
          isLoadingMoreMessages={isLoadingMoreMessages}
          onLoadMoreMessages={onLoadMoreMessages}
          onDeleteMessage={onDeleteMessage}
          onRefetchConversationDetails={onRefetchConversationDetails}
          onComposeChannelChange={handleComposeChannelChange}
        />
      ) : awaitingConversationDetails ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 200,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        !isMobile && <Placeholder />
      )}

      <PromoteToRequestDialog
        open={promoteOpen}
        onClose={() => setPromoteOpen(false)}
        onSubmit={handlePromoteSubmit}
        isLoading={promoteLoading}
        currentUser={userData}
        messages={messages}
        peerPhone={peerPhone}
        peerDisplayName={selectedConversation?.name || selectedConversation?.nome || ""}
        peerAvatar={selectedConversation?.profile_picture || null}
        error={promoteError}
      />

      <DuplicateClientDialog
        open={messagesDupOpen}
        duplicates={messagesSyncError?.duplicates || []}
        attemptedPayload={mergeAttemptedPayload}
        secondaryClientId={mergeSecondaryClientId}
        secondaryLabel="cliente vinculado a esta conversa"
        onClose={() => setMessagesDupOpen(false)}
        onChangePhone={() => {
          setMessagesDupOpen(false);
          onShowDetails?.();
        }}
        onMerged={async () => {
          setMessagesDupOpen(false);
          if (typeof onRefetchConversationDetails === "function") {
            await onRefetchConversationDetails();
          }
          if (typeof reloadConversationMessages === "function") {
            await reloadConversationMessages();
          }
        }}
      />

      <DuplicateClientDialog
        open={clientDupDialog.open}
        duplicates={clientDupDialog.duplicates}
        attemptedPayload={clientDupDialog.attemptedPayload}
        secondaryClientId={clientDupDialog.secondaryClientId}
        secondaryLabel="cliente desta conversa"
        onClose={() => setClientDupDialog((s) => ({ ...(s || {}), open: false }))}
        onChangePhone={() => {
          setClientDupDialog((s) => ({ ...(s || {}), open: false }));
          onShowDetails?.();
        }}
        onMerged={async () => {
          setClientDupDialog((s) => ({ ...(s || {}), open: false }));
          if (typeof onRefetchConversationDetails === "function") {
            await onRefetchConversationDetails();
          }
        }}
      />

      <Dialog open={deletePersonalOpen} onClose={() => !deletePersonalBusy && setDeletePersonalOpen(false)}>
        <DialogTitle>{t("pane.deletePersonalConfirmTitle")}</DialogTitle>
        <DialogContent>{t("pane.deletePersonalConfirmBody")}</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePersonalOpen(false)} disabled={deletePersonalBusy}>
            {t("pane.deletePersonalCancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deletePersonalBusy}
            onClick={async () => {
              if (typeof onDeletePersonalConversation !== "function") return;
              setDeletePersonalBusy(true);
              try {
                await onDeletePersonalConversation();
                setDeletePersonalOpen(false);
              } catch {
                /* snackbar no pai */
              } finally {
                setDeletePersonalBusy(false);
              }
            }}
          >
            {t("pane.deletePersonalConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MessageHistoryPane;
